import { Button, Input, Typography } from '@smartgift/frontend-toolkit';
import { useFormik } from 'formik';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import isEmailValidator from 'validator/lib/isEmail';
import * as yup from 'yup';

import { FORM_ERROR_MESSAGES, MAX_EMAIL_LENGTH } from '@hero/constants';
import { usePurifyTranslation } from '@hero/hooks';
import { useCheckUserExists } from '@hero/services/organization-service/hooks';
import { handleRedirectSignIn } from '@hero/utils';

import { Advantages } from '../Advantages';

import * as S from './style';

function SignUpEmail({ activeStep, setActiveStep, signUpData, setSignUpData }) {
  const { t } = useTranslation();
  const { purifyT } = usePurifyTranslation();
  const { mutateAsync: checkUserExistence, isLoading } = useCheckUserExists();

  const [hasError, setHasError] = useState(false);
  const validationSchema = yup.object().shape({
    email: yup
      .string()
      .email(FORM_ERROR_MESSAGES.invalidEmail)
      .max(MAX_EMAIL_LENGTH, FORM_ERROR_MESSAGES.max50Char)
      .required(FORM_ERROR_MESSAGES.emailRequired)
      .test(
        'is-valid',
        () => FORM_ERROR_MESSAGES.invalidEmail,
        value =>
          value
            ? isEmailValidator(value)
            : new yup.ValidationError(FORM_ERROR_MESSAGES.invalidEmail)
      )
  });

  const formik = useFormik({
    initialValues: {
      email: signUpData?.email ?? ''
    },
    validationSchema,
    validateOnChange: false,
    onSubmit: async data => {
      setHasError(false);

      try {
        const res = await checkUserExistence({
          email: data.email
        });
        setSignUpData({ ...data, verificationToken: res.verifyToken });
        setActiveStep(activeStep + 1);
      } catch (error) {
        setHasError(true);
      }
    }
  });

  return (
    <S.SignUpEmail>
      <S.SignUpEmailForm>
        <Typography variant="subtitle3Bold">{t('signUpTitle')}</Typography>

        <S.SignUpEmailFormBody>
          <Typography variant="body1">{t('signUpDescription')}</Typography>
          <Input
            variant="outlined"
            label={`${t('enterEmail')}*`}
            name="email"
            onChange={e => {
              formik.handleChange(e);
              hasError && setHasError(false);
            }}
            value={formik.values.email}
            error={formik.errors.email || hasError}
            disabled={isLoading}
            inputProps={{ maxLength: MAX_EMAIL_LENGTH }}
          />

          <Button
            color="primary"
            disabled={!formik.isValid || !formik.values.email || isLoading}
            loading={isLoading}
            onClick={formik.handleSubmit}
            block
          >
            {t('createAccount')}
          </Button>
        </S.SignUpEmailFormBody>
        <S.SignUpEmailFormFooter>
          <Typography variant="body2" color="fg" emphasis="weaker">
            {t('getStartedDescription')}
          </Typography>
          <S.SignInWrapper>
            <Typography variant="body2" as="div">
              {t('alreadyCustomer')}
            </Typography>
            <Button color="primary" onClick={() => handleRedirectSignIn()} block variant="outlined">
              {t('signIn')}
            </Button>
          </S.SignInWrapper>
        </S.SignUpEmailFormFooter>
        <S.SignUpEmailFormNote>
          <Typography
            variant="caption1"
            dangerouslySetInnerHTML={{ __html: purifyT('getStartedNote') }}
          />
        </S.SignUpEmailFormNote>
      </S.SignUpEmailForm>

      <Advantages />
    </S.SignUpEmail>
  );
}

export { SignUpEmail };
